import { useState } from "react";
import { Avatar } from "@mui/material";
import { HelpTerm } from "./HelpTerm";
import {
  difference,
  fixtureLayers,
  formatModelNumber,
  goalTotal,
} from "../helpers/statisticModelDisplay";
// Inline detail section of the existing StatisticModel workspace, not a second dialog.
export default function StatisticModelFixture({
  result,
  fixture,
  comparison,
  leagueName,
  seasonName,
  en,
}) {
  const [deviations, setDeviations] = useState(false),
    [reference, setReference] = useState("statistics"),
    [absolute, setAbsolute] = useState(false),
    [explain, setExplain] = useState(false);
  const t = (de, eng) => (en ? eng : de),
    fmt = (n, options) => formatModelNumber(n, en, options),
    pre = result.pre,
    post = fixture?.final && result.evaluation;
  const layers = fixtureLayers(result, comparison, fixture);
  const labels = {
    statistics: t("Statistikbasiertes Modell", "Statistics-based model"),
    result: t("Ergebnisbasiertes Modell", "Results-based model"),
    market: t("Markt-Rückrechnung", "Market-implied goals"),
    should: "Should Goals",
    actual: t("Tatsächliche Tore", "Actual goals"),
  };
  const terms = {
    statistics: "statisticsModel",
    result: "resultModel",
    market: "marketExpectation",
    should: "shouldGoals",
  };
  const base = layers.find((r) => r.id === reference);
  const jump = (id) =>
    document
      .getElementById(id)
      ?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  const names = {
    home:
      fixture?.home_name ||
      fixture?.name?.split(/ vs | – /)[0] ||
      t("Heim", "Home"),
    away:
      fixture?.away_name ||
      fixture?.name?.split(/ vs | – /)[1] ||
      t("Auswärts", "Away"),
  };
  const pairHeader = (side) => (
    <span className="sm-team">
      <Avatar
        variant="square"
        src={fixture?.[side + "_image_path"]}
        alt={names[side]}
        sx={{ width: 32, height: 32 }}
      >
        {names[side]?.[0]}
      </Avatar>
      {names[side]}
    </span>
  );
  const oddsKeys = ["homewinprob", "drawprob", "awaywinprob"];
  return (
    <article className="sm-fixture-detail" id="sm-fixture-detail">
      <header>
        <h3 className="sm-pairing">
          {pairHeader("home")}
          <span>
            {post
              ? `${fmt(fixture.goals?.home ?? post.actual_goals?.home, {
                  digits: 0,
                })} : ${fmt(fixture.goals?.away ?? post.actual_goals?.away, {
                  digits: 0,
                })}`
              : "–"}
          </span>
          {pairHeader("away")}
        </h3>
        <p>
          {fixture?.kickoff &&
            new Date(fixture.kickoff).toLocaleString(en ? "en-GB" : "de-DE", {
              dateStyle: "medium",
              timeStyle: "short",
            })}{" "}
          · {leagueName || "—"} · {seasonName || "—"}
          {fixture?.round_name
            ? ` · ${t("Spieltag", "Round")} ${fixture.round_name}`
            : ""}{" "}
          · {post ? t("Abgeschlossen", "Finished") : "Pre-Match"} ·{" "}
          {pre.kind === "historical_replay"
            ? t("Historische Simulation", "Historical simulation")
            : t(
                "Gespeicherte Pre-Match-Prognose",
                "Stored pre-match prediction"
              )}
        </p>
      </header>
      <nav
        className="sm-controls"
        aria-label={t("Analyseaktionen", "Analysis actions")}
      >
        <button onClick={() => jump("sm-goal-comparison")}>
          {t("Modelle vergleichen", "Compare models")}
        </button>
        <button onClick={() => jump("sm-statistics-comparison")}>
          {t("Statistiken vergleichen", "Compare statistics")}
        </button>
        <button
          onClick={() => {
            const el = document.getElementById("sm-calculation");
            if (el) el.open = true;
            jump("sm-calculation");
          }}
        >
          {t("Rechenweg", "Calculation")}
        </button>
        <button
          aria-pressed={deviations}
          onClick={() => setDeviations(!deviations)}
        >
          {t("Abweichungen anzeigen", "Show differences")}
        </button>
        <button
          className="sm-explain"
          aria-expanded={explain}
          onClick={() => setExplain(!explain)}
        >
          {t("Was sagen mir diese Zahlen?", "What do these numbers tell me?")}
        </button>
      </nav>
      {explain && (
        <aside className="sm-explanation">
          <p>
            {t(
              "Die Modelle liefern unabhängige Torerwartungen. Die Markt-Rückrechnung ergänzt die Perspektive der gespeicherten Quoten. Should beschreibt das Spiel anhand tatsächlicher Statistiken mit eingefrorener Conversion. Eine Abweichung ist keine Qualitätsbewertung.",
              "The models provide independent goal expectations. Market-implied goals add the perspective of stored odds. Should describes the match using actual statistics and frozen conversion. A difference is not a quality rating."
            )}
          </p>
          <p>
            {t(
              "Noch keine fixturebezogene Josie-/KI-Auswertung angebunden. Es wird kein KI-Aufruf gestartet. Die vollständige Datengrundlage steht im Rechenweg bereit.",
              "No fixture-specific Josie/AI interpretation is connected yet. No AI request is made. The complete analysis context is available in the calculation details."
            )}
          </p>
          <a
            href="/help?target=tableComparison"
            target="_blank"
            rel="noreferrer"
          >
            {t("Hilfe: Expected / Actual", "Help: expected / actual")}
          </a>
        </aside>
      )}
      <h4 id="sm-goal-comparison">
        {t("Torerwartungen im Vergleich", "Goal comparison")}
      </h4>
      {deviations && (
        <p>
          <label>
            {t("Referenz", "Reference")}:{" "}
            <select
              aria-label={t("Referenz", "Reference")}
              value={reference}
              onChange={(e) => setReference(e.target.value)}
            >
              {layers
                .filter((r) => r.id !== "actual")
                .map((r) => (
                  <option key={r.id} value={r.id}>
                    {labels[r.id]}
                  </option>
                ))}
            </select>
          </label>{" "}
          ·{" "}
          {t(
            "Abweichung = angezeigter Wert − Referenz",
            "Difference = displayed value − reference"
          )}
          <br />
          <HelpTerm termKey="modelDifference">
            {t(
              "+ höher, − niedriger; keine Gut-/Schlecht-Bewertung",
              "+ higher, − lower; no good/bad assessment"
            )}
          </HelpTerm>
        </p>
      )}
      <div className="sm-grid">
        <table
          aria-label={t("Fixture-Modellvergleich", "Fixture model comparison")}
        >
          <thead>
            <tr>
              <th>{t("Vergleichsebene", "Comparison layer")}</th>
              {["home", "away", "total"].map((s) => (
                <th key={s}>
                  {s === "home"
                    ? t("Heim", "Home")
                    : s === "away"
                    ? t("Auswärts", "Away")
                    : t("Summe", "Total")}
                </th>
              ))}
              {deviations && (
                <>
                  <th>Δ {t("Heim", "Home")}</th>
                  <th>Δ {t("Auswärts", "Away")}</th>
                  <th>Δ {t("Summe", "Total")}</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {layers.map((r) => (
              <tr key={r.id}>
                <th>
                  <HelpTerm termKey={terms[r.id]}>{labels[r.id]}</HelpTerm>
                </th>
                <td>
                  {fmt(r.value?.home, { digits: r.id === "actual" ? 0 : 2 })}
                </td>
                <td>
                  {fmt(r.value?.away, { digits: r.id === "actual" ? 0 : 2 })}
                </td>
                <td>
                  {fmt(r.total ?? goalTotal(r.value), {
                    digits: r.id === "actual" ? 0 : 2,
                  })}
                </td>
                {deviations && (
                  <>
                    {["home", "away"].map((s) => (
                      <td key={s}>
                        {fmt(difference(r.value?.[s], base?.value?.[s]), {
                          signed: true,
                        })}
                      </td>
                    ))}
                    <td>
                      {fmt(
                        difference(
                          r.total ?? goalTotal(r.value),
                          base?.total ?? goalTotal(base?.value)
                        ),
                        { signed: true }
                      )}
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p>
        {t(
          "— = gespeicherter Wert fehlt. Marktwerte sind gespeicherte Rückrechnungen, keine neu berechneten Live-Quoten.",
          "— = stored value missing. Market values are stored inferences, not newly calculated live odds."
        )}
      </p>
      <h4>
        <HelpTerm termKey="oneXTwo">1 / X / 2</HelpTerm>
      </h4>
      <div className="sm-grid">
        <table aria-label="1X2">
          <thead>
            <tr>
              <th>{t("Modell / Wert", "Model / value")}</th>
              <th>1</th>
              <th>X</th>
              <th>2</th>
            </tr>
          </thead>
          <tbody>
            {[
              [labels.statistics, pre],
              [labels.result, comparison?.result_model],
            ].flatMap(([label, m]) =>
              [
                ["probabilities", t("Wahrscheinlichkeit", "Probability")],
                ["fair_odds", "Fair Odds"],
              ].map(([key, name]) => (
                <tr key={label + key}>
                  <th>
                    {label} ·{" "}
                    <HelpTerm
                      termKey={key === "fair_odds" ? "fairOdds" : "oneXTwo"}
                    >
                      {name}
                    </HelpTerm>
                  </th>
                  {oddsKeys.map((k) => (
                    <td key={k}>
                      {fmt(m?.[key]?.[k], { percent: key === "probabilities" })}
                    </td>
                  ))}
                </tr>
              ))
            )}
            <tr>
              <th>{t("Gespeicherte Marktquote", "Stored market odds")}</th>
              {["1", "X", "2"].map((k) => (
                <td key={k}>{fmt(comparison?.market?.odds?.[k])}</td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      <h4 id="sm-statistics-comparison">
        {t("Statistiken vergleichen", "Compare statistics")}
      </h4>
      <p>
        <HelpTerm termKey="modelDifference">Δ = Actual − Expected</HelpTerm>
        {post && (
          <label>
            {" "}
            ·{" "}
            <input
              type="checkbox"
              checked={absolute}
              onChange={(e) => setAbsolute(e.target.checked)}
            />
            {t("Absolute Abweichung", "Absolute error")}
          </label>
        )}
      </p>
      <div className="sm-grid">
        <table aria-label={t("Fixture-Statistiken", "Fixture statistics")}>
          <thead>
            <tr>
              <th>{t("Statistik", "Statistic")}</th>
              {["home", "away"].flatMap((side) => [
                <th key={side + "e"}>
                  <HelpTerm termKey="expectedStatistic">
                    {side === "home"
                      ? t("Heim erwartet", "Home expected")
                      : t("Auswärts erwartet", "Away expected")}
                  </HelpTerm>
                </th>,
                ...(post
                  ? [
                      <th key={side + "a"}>
                        <HelpTerm termKey="actualStatistic">
                          {side === "home"
                            ? t("Heim tatsächlich", "Home actual")
                            : t("Auswärts tatsächlich", "Away actual")}
                        </HelpTerm>
                      </th>,
                      <th key={side + "d"}>Δ (Actual − Expected)</th>,
                      ...(absolute ? [<th key={side + "abs"}>|Δ|</th>] : []),
                    ]
                  : []),
              ])}
            </tr>
          </thead>
          <tbody>
            {Object.entries(pre.expected_statistics || {}).map(([id, v]) => (
              <tr key={id}>
                <th>{v.title}</th>
                {["home", "away"].flatMap((side) => {
                  const a = post?.actual_statistics?.[id]?.[side],
                    d = difference(a, v[side]),
                    percent = v.unit === "ratio" || id === "45";
                  return [
                    <td key={side + "e"}>{fmt(v[side], { percent })}</td>,
                    ...(post
                      ? [
                          <td key={side + "a"}>{fmt(a, { percent })}</td>,
                          <td key={side + "d"}>
                            {fmt(d === null ? null : d * (percent ? 100 : 1), { signed: true })}
                            {percent ? t(" Pp.", " pp.") : ""}
                          </td>,
                          ...(absolute
                            ? [
                                <td key={side + "abs"}>
                                  {fmt(d === null ? null : Math.abs(d), {
                                    percent,
                                  })}
                                </td>,
                              ]
                            : []),
                        ]
                      : []),
                  ];
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="sm-grid">
        <table aria-label={t("Weitere Modellwerte", "Additional model values")}>
          <thead>
            <tr>
              <th>{t("Wert", "Value")}</th>
              <th>{t("Heim", "Home")}</th>
              <th>{t("Auswärts", "Away")}</th>
            </tr>
          </thead>
          <tbody>
            {[
              [
                "Attack Moments",
                pre.expected_attack_moments,
                "attackMoments",
                false,
              ],
              [
                "Expected Conversion",
                pre.expected_conversion,
                "conversionRate",
                true,
              ],
              ...(post
                ? [
                    [
                      "Actual Attack Moments",
                      post.actual_attack_moments,
                      "attackMoments",
                      false,
                    ],
                    [
                      "Actual Conversion",
                      post.actual_conversion,
                      "conversionRate",
                      true,
                    ],
                    ["Provider xG", post.provider_xg, null, false],
                  ]
                : []),
            ].map(([label, v, term, percent]) => (
              <tr key={label}>
                <th>
                  <HelpTerm termKey={term}>{label}</HelpTerm>
                </th>
                <td>{fmt(v?.home, { percent })}</td>
                <td>{fmt(v?.away, { percent })}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!!post?.gaps?.length && (
        <p role="status">
          {t("Datenlücken", "Data gaps")}: {post.gaps.join(", ")}
        </p>
      )}
      <details id="sm-calculation">
        <summary>
          {t("Statistiken und Rechenweg", "Statistics and calculation")} ·
          Fixture {pre.fixture_id}
        </summary>
        <p>
          {t(
            "Gespeicherte Daten in voller Präzision; Marktquelle, Fit-Fehler und Parameterstand bleiben erhalten.",
            "Stored data at full precision; market provenance, fit error and parameter version are preserved."
          )}
        </p>
        <pre>{JSON.stringify({ result, comparison, fixture }, null, 2)}</pre>
      </details>
    </article>
  );
}
